import { groupIntoMeasures, getBeatsPerMeasure, BEAT_VALUES } from './musicTheory.js'

const PLAYABLE_PC = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B']
const NOTE_PC = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7,
  'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11,
}

const BEAT_EPS = 0.0001

/** Salamander 샘플 이름에 맞게 정규화 (App·재생 공통) */
export function toPlayableForSampler(pitch) {
  // Cn4 형태의 ♮ 임시표는 음높이 자체는 자연음과 동일하므로 n 제거
  const normalized = String(pitch).replace(/^([A-G])n(\d)$/, '$1$2')
  const m = normalized.match(/^([A-G][#b]?)(\d)$/)
  if (!m) return normalized
  const pc = NOTE_PC[m[1]]
  return pc !== undefined ? PLAYABLE_PC[pc] + m[2] : normalized
}

/** 한 성부 마디 안에서 (마디 시작 기준) 음표 시작 시점과 피치 */
function listOnsetsInMeasure(notes) {
  if (!notes?.length) return []
  let beat = 0
  const out = []
  for (const n of notes) {
    const nb = BEAT_VALUES[n.duration] ?? 1
    if (!n.isRest && n.pitch) {
      out.push({ beat, pitch: n.pitch })
    }
    beat += nb
  }
  return out
}

function mergeCloseBeats(beats, eps = BEAT_EPS) {
  const s = [...beats].sort((a, b) => a - b)
  const out = []
  for (const b of s) {
    if (out.length === 0 || Math.abs(b - out[out.length - 1]) >= eps) {
      out.push(b)
    }
  }
  return out
}

function pitchAtBeat(onsets, b) {
  const o = onsets.find((x) => Math.abs(x.beat - b) < BEAT_EPS)
  return o ? o.pitch : null
}

/**
 * 마디·박 단위로 4성부 동시 발음 시점을 모은다.
 * 각 이벤트 = 해당 마디 안에서 같은 시점(박)에 시작하는 음들만 모은 화음.
 * (한 마디에 4박자 같은 화음이면 4번의 이벤트가 생김)
 */
export function getHarmonyPlaybackEvents(score, timeSignature) {
  const beatsPerMeasure = getBeatsPerMeasure(timeSignature)
  const sopMeas = groupIntoMeasures(score.soprano ?? [], beatsPerMeasure)
  const altMeas = groupIntoMeasures(score.alto ?? [], beatsPerMeasure)
  const tenMeas = groupIntoMeasures(score.tenor ?? [], beatsPerMeasure)
  const basMeas = groupIntoMeasures(score.bass ?? [], beatsPerMeasure)
  const n = Math.max(sopMeas.length, altMeas.length, tenMeas.length, basMeas.length, 1)

  const events = []
  for (let i = 0; i < n; i++) {
    const sopOn = listOnsetsInMeasure(sopMeas[i]?.notes)
    const altOn = listOnsetsInMeasure(altMeas[i]?.notes)
    const tenOn = listOnsetsInMeasure(tenMeas[i]?.notes)
    const basOn = listOnsetsInMeasure(basMeas[i]?.notes)

    const allBeats = mergeCloseBeats([
      ...sopOn.map((o) => o.beat),
      ...altOn.map((o) => o.beat),
      ...tenOn.map((o) => o.beat),
      ...basOn.map((o) => o.beat),
    ])

    for (const b of allBeats) {
      const pitches = []
      const pS = pitchAtBeat(sopOn, b)
      const pA = pitchAtBeat(altOn, b)
      const pT = pitchAtBeat(tenOn, b)
      const pB = pitchAtBeat(basOn, b)
      if (pS) pitches.push(pS)
      if (pA) pitches.push(pA)
      if (pT) pitches.push(pT)
      if (pB) pitches.push(pB)
      if (pitches.length > 0) {
        events.push({
          measureIndex: i,
          measure: i + 1,
          beatInMeasure: b,
          pitches,
          voices: { soprano: pS, alto: pA, tenor: pT, bass: pB },
        })
      }
    }
  }
  return events
}
